import { useQuery } from '@apollo/client'
import { BestPracticeAuditHistoryQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useSelectedOMVersion } from 'components/WorkspaceEditor/ActionBarStore'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useBestPracticeManagementStore } from 'components/WorkspaceEditor/features/bestPractices/store/BestPracticeManagementStore'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import {
    BestPracticeTip,
    BPTipAction,
    BPTipParameters,
    BPVersion,
    parameterConfig,
    ParameterKey,
} from '../types/bpManagement.types'

interface AuditConfigurationSnapshot {
    isGlobal: boolean
    projectGroup?: string | null
    owner?: string | null
    buildingType?: string | null
    sustainabilityType?: string | string[] | null
    contractType?: string | null
    constructionType?: string | null
    geographicRegion?: string | null
    seismicZone?: string | null
    windZone?: string | null
    cityRegionCountry?: string | null
}

interface AuditUserProfile {
    firstName?: string | null
    lastName?: string | null
}

interface AuditUser {
    id?: string | null
    email?: string | null
    profileInfo?: AuditUserProfile | null
}

interface AuditEntry {
    id: string
    action: BPTipAction
    timestamp: string
    contentItemId?: string | null
    appliedContentItemId?: string | null
    userId?: string | null
    user?: AuditUser | null
    configuration?: AuditConfigurationSnapshot | null
    changeDescription?: string | null
    version?: BPVersion | null
    bestPracticeId: string
}

const buildAuthorName = (entry: AuditEntry): string => {
    const profile = entry.user?.profileInfo
    const first = profile?.firstName?.trim()
    const last = profile?.lastName?.trim()
    if (first || last) return [first, last].filter(Boolean).join(' ')
    if (entry.user?.email) return entry.user.email
    return entry.userId || 'Unknown user'
}

interface AuditHistoryResponse {
    getBestPracticeAuditHistory: AuditEntry[]
}

// Bridges the FE ParameterKey union with the BE AuditConfigurationSnapshot
// field names. `sustainability` ↔ `sustainabilityType` diverges; `city` and
// `provinceState` are write-only FE fields — the BE returns the combined value
// as `cityRegionCountry`, which maps to the `cityRegionCountry` FE param key.
const FE_TO_BE_PARAM_KEY: Partial<Record<
    ParameterKey,
    Exclude<keyof AuditConfigurationSnapshot, 'isGlobal'>
>> = {
    projectGroup: 'projectGroup',
    owner: 'owner',
    buildingType: 'buildingType',
    constructionType: 'constructionType',
    contractType: 'contractType',
    sustainability: 'sustainabilityType',
    geographicRegion: 'geographicRegion',
    seismicZone: 'seismicZone',
    windZone: 'windZone',
    cityRegionCountry: 'cityRegionCountry',
}

const snapshotToParameters = (
    snapshot: AuditConfigurationSnapshot | null | undefined
): BPTipParameters | undefined => {
    if (!snapshot || snapshot.isGlobal) return undefined
    const params: BPTipParameters = {}
    parameterConfig.forEach(({ key }) => {
        const beKey = FE_TO_BE_PARAM_KEY[key]
        if (!beKey) return
        const raw = snapshot[beKey]
        // sustainabilityType (and potentially others) may come as string[] from the BE
        const value = Array.isArray(raw)
            ? raw.length > 0
                ? raw[0]
                : null
            : raw
        if (value) params[key] = value
    })
    return Object.keys(params).length > 0 ? params : undefined
}

const entryToTip = (entry: AuditEntry): BestPracticeTip => {
    const snapshot = entry.configuration
    const isGlobal = snapshot?.isGlobal ?? false
    return {
        id: entry.id,
        action: entry.action,
        type: isGlobal ? 'global' : 'parameter',
        author: { name: buildAuthorName(entry) },
        occurredAt: entry.timestamp,
        contentItemId: entry.contentItemId ?? null,
        appliedContentItemId: entry.appliedContentItemId ?? null,
        parameters: snapshotToParameters(snapshot),
        changeDescription: entry.changeDescription || undefined,
        version: entry.version ?? null,
        bestPracticeId: entry.bestPracticeId,
    }
}

interface UseBestPracticeAuditHistoryResult {
    tips: BestPracticeTip[]
    loading: boolean
    error: boolean
    /** True when no iterationId is available — the query can't fire. */
    waitingForIteration: boolean
    refetch: () => void
}

/**
 * Fetches the BP audit history for the iteration the user is currently viewing
 * in the OM workspace. Gated behind the OM BP feature flag + OM workspace
 * check so the query never fires outside the supported context. Returns tips
 * newest-first as the BE returns them.
 */
export const useBestPracticeAuditHistory = (): UseBestPracticeAuditHistoryResult => {
    const { projectId } = useParams<{ projectId: string }>()
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const selectedOMVersion = useSelectedOMVersion()
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const featureEnabled =
        isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace
    const iterationId =
        selectedProjectElementNode?.iteration?.id ??
        selectedOMVersion?.elementNodes?.find(
            (n) =>
                n.treePath === selectedProjectElementNode?.treePath ||
                n.id === selectedProjectElementNode?.id
        )?.iteration?.id ??
        selectedOMVersion?.elementNodes?.find((n) => n.iteration?.id)?.iteration
            ?.id
    const contentItemId = useBestPracticeManagementStore(
        (s) => s.auditContentItemFilter
    )
    const setAuditContentItemFilter = useBestPracticeManagementStore(
        (s) => s.setAuditContentItemFilter
    )
    const setPreviewSnapshot = useBestPracticeManagementStore(
        (s) => s.setPreviewSnapshot
    )

    // Restore any active version preview and clear filters when the panel unmounts.
    useEffect(() => {
        return () => {
            const snapshot = useBestPracticeManagementStore.getState()
                .previewSnapshot
            if (snapshot) {
                const nodes = useWorkspace2Store.getState().contentItemViewNodes
                const restored = nodes.map((n) =>
                    n.id === snapshot.contentItemId
                        ? { ...n, elements: snapshot.originalElements }
                        : n
                )
                useWorkspace2Store
                    .getState()
                    .actions.setContentItemViewNodes(restored)
                setPreviewSnapshot(null)
            }
            setAuditContentItemFilter(null)
        }
    }, [setAuditContentItemFilter, setPreviewSnapshot])

    const { data, loading, error, refetch } = useQuery<AuditHistoryResponse>(
        BestPracticeAuditHistoryQuery,
        {
            variables: {
                iterationId,
                contentItemId,
                projectId,
                projectElementNodeId: selectedProjectElementNode?.id,
            },
            skip: !featureEnabled || !iterationId || !projectId,
            fetchPolicy: 'cache-and-network',
        }
    )

    if (!featureEnabled) {
        return {
            tips: [],
            loading: false,
            error: false,
            waitingForIteration: false,
            refetch: () => undefined,
        }
    }

    return {
        tips: (data?.getBestPracticeAuditHistory ?? []).map(entryToTip),
        loading,
        error: Boolean(error),
        waitingForIteration: !iterationId,
        refetch,
    }
}
